import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  Alert,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LocalShipping as ShippingIcon,
} from '@mui/icons-material';
import { clientShippingAddressesAPI } from '../../services/api';

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const emptyAddress = {
  name: '',
  company: '',
  contactName: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  isDefault: false,
};

const ClientShippingAddresses = ({ client, onAddressUpdated }) => {
  const [additionalAddresses, setAdditionalAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyAddress);

  // Build primary address from client profile
  const primaryAddress = client?.profile?.address ? {
    id: 'primary',
    isPrimary: true,
    name: 'Primary Address',
    company: client.profile?.clinicName || '',
    contactName: client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : '',
    address1: client.profile?.address || '',
    address2: '',
    city: client.profile?.city || '',
    state: client.profile?.state || '',
    zipCode: client.profile?.zipCode || '',
    phone: client.phone || '',
    isDefault: true, // Primary is always considered default if no other default exists
  } : null;

  // Combine primary + additional addresses
  const addresses = primaryAddress 
    ? [primaryAddress, ...additionalAddresses]
    : additionalAddresses;

  useEffect(() => {
    if (client?.id) {
      loadAddresses();
    }
  }, [client?.id]);

  const loadAddresses = async () => {
    if (!client?.id) return;
    
    try {
      setLoading(true);
      const response = await clientShippingAddressesAPI.getAddresses(client.id);
      if (response.success && response.data) {
        setAdditionalAddresses(response.data);
      } else {
        setAdditionalAddresses([]);
      }
    } catch (error) {
      console.error('Failed to load addresses:', error);
      setError('Failed to load shipping addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setFormData({
        name: address.name || '',
        company: address.company || '',
        contactName: address.contactName || '',
        address1: address.address1 || '',
        address2: address.address2 || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.zipCode || '',
        phone: address.phone || '',
        isDefault: address.isDefault || false,
      });
    } else {
      setEditingAddress(null);
      setFormData({ ...emptyAddress, isDefault: additionalAddresses.length === 0 && !primaryAddress });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAddress(null);
    setFormData(emptyAddress);
    setError('');
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name || !formData.address1 || !formData.city || !formData.state || !formData.zipCode) {
      setError('Please fill in all required fields (Name, Address, City, State, Zip)');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (editingAddress) {
        // Update existing address
        const response = await clientShippingAddressesAPI.updateAddress(editingAddress.id, formData);
        if (response.success) {
          setSuccess('Address updated successfully');
          loadAddresses();
          handleCloseDialog();
          if (onAddressUpdated) onAddressUpdated();
        } else {
          throw new Error(response.error || 'Failed to update address');
        }
      } else {
        // Create new address
        const response = await clientShippingAddressesAPI.addAddress({
          userId: client.id,
          ...formData,
        });
        if (response.success) {
          setSuccess('Address added successfully');
          loadAddresses();
          handleCloseDialog();
          if (onAddressUpdated) onAddressUpdated();
        } else {
          throw new Error(response.error || 'Failed to add address');
        }
      }
    } catch (error) {
      console.error('Failed to save address:', error);
      setError(error.message || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this shipping address?')) return;

    try {
      const response = await clientShippingAddressesAPI.deleteAddress(addressId);
      if (response.success) {
        setSuccess('Address deleted successfully');
        loadAddresses();
        if (onAddressUpdated) onAddressUpdated();
      } else {
        throw new Error(response.error || 'Failed to delete address');
      }
    } catch (error) {
      console.error('Failed to delete address:', error);
      setError(error.message || 'Failed to delete address');
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const response = await clientShippingAddressesAPI.setDefault(addressId);
      if (response.success) {
        setSuccess('Default address updated');
        loadAddresses();
        if (onAddressUpdated) onAddressUpdated();
      } else {
        throw new Error(response.error || 'Failed to set default');
      }
    } catch (error) {
      console.error('Failed to set default:', error);
      setError(error.message || 'Failed to set default address');
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShippingIcon sx={{ color: '#039582' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
            Shipping Addresses
          </Typography>
          <Chip label={addresses.length} size="small" />
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderColor: '#039582', color: '#039582' }}
        >
          Add Address
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : addresses.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No shipping addresses on file
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add shipping addresses that can be used for orders
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {addresses.map((address) => (
            <Grid item xs={12} md={6} key={address.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: address.isPrimary ? '#1976d2' : address.isDefault ? '#039582' : undefined,
                  borderWidth: (address.isPrimary || address.isDefault) ? 2 : 1,
                  position: 'relative',
                  bgcolor: address.isPrimary ? '#f8fafc' : undefined,
                }}
              >
                {address.isPrimary ? (
                  <Chip
                    label="Primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: '#1976d2',
                      color: 'white',
                    }}
                  />
                ) : address.isDefault && (
                  <Chip
                    label="Default"
                    size="small"
                    icon={<StarIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: '#039582',
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' },
                    }}
                  />
                )}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, pr: (address.isPrimary || address.isDefault) ? 8 : 0 }}>
                  {address.name}
                </Typography>
                {address.company && (
                  <Typography variant="body2" color="text.secondary">
                    {address.company}
                  </Typography>
                )}
                {address.contactName && (
                  <Typography variant="body2" color="text.secondary">
                    c/o {address.contactName}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {address.address1}
                  {address.address2 && <>, {address.address2}</>}
                </Typography>
                <Typography variant="body2">
                  {address.city}, {address.state} {address.zipCode}
                </Typography>
                {address.phone && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    📞 {address.phone}
                  </Typography>
                )}
                
                <Divider sx={{ my: 1.5 }} />
                
                {address.isPrimary ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Edit via "Edit Client" button above
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!address.isDefault && (
                      <Tooltip title="Set as Default">
                        <IconButton
                          size="small"
                          onClick={() => handleSetDefault(address.id)}
                          sx={{ color: '#9ca3af' }}
                        >
                          <StarBorderIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(address)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(address.id)}
                        sx={{ color: '#dc2626' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Address Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAddress ? 'Edit Shipping Address' : 'Add Shipping Address'}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Label"
                placeholder="e.g., Main Clinic, Satellite Office"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address1}
                onChange={(e) => setFormData({ ...formData, address1: e.target.value })}
                required
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Apt, Suite, Unit (optional)"
                value={formData.address2}
                onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
                disabled={saving}
              >
                {US_STATES.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                required
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.name || !formData.address1 || !formData.city || !formData.state || !formData.zipCode}
            sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
          >
            {saving ? 'Saving...' : (editingAddress ? 'Update Address' : 'Add Address')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ClientShippingAddresses;

